/**
 * Hooks for case management
 */

import { useState, useEffect, useCallback } from 'react'
import type {
  Case,
  CasesByStatus,
  CaseStatus,
  CreateCaseInput,
  UpdateCaseInput,
  ListCasesParams,
  CaseTeamMember,
  CaseTeamRole,
} from '../types/case-management'
import {
  listCases,
  listCasesGrouped,
  getCase,
  createCase,
  updateCase,
  updateCaseStatus,
  deleteCase,
  listCaseTeam,
  addTeamMember,
  removeTeamMember,
} from '../services/cases'

// =============================================================================
// useCases - List and filter cases
// =============================================================================

interface UseCasesResult {
  cases: Case[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * Hook to fetch and filter cases
 */
export function useCases(params?: ListCasesParams): UseCasesResult {
  const [cases, setCases] = useState<Case[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Destructure primitive fields from `params` so the dep array below depends on
  // stable scalar values, not on the object reference. Callers commonly pass a
  // fresh object literal (`{ page, perPage }`) every render — including the
  // object itself in the dep array recreates `fetchData` on every render, which
  // in turn re-fires the `useEffect([fetchData])` below, triggering an infinite
  // fetch loop that exhausts the browser's HTTP socket pool
  // (net::ERR_INSUFFICIENT_RESOURCES).
  const status = params?.status
  const priority = params?.priority
  const clientProfileId = params?.clientProfileId
  const search = params?.search
  const limit = params?.limit
  const offset = params?.offset

  const fetch = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      // Pass `params` through (not a reconstructed literal) to preserve the
      // shape's distinction between "absent" and "explicit undefined" under
      // exactOptionalPropertyTypes. Primitive fields are in the dep array
      // below so the closure captures fresh `params` whenever any field
      // actually changes.
      const data = await listCases(params)
      setCases(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch cases')
      setCases([])
    } finally {
      setLoading(false)
    }
    // Intentionally omits `params` (unstable object reference); we depend on
    // each primitive field instead. See the comment block above the destructuring.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, priority, clientProfileId, search, limit, offset])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return { cases, loading, error, refetch: fetch }
}

// =============================================================================
// useCasesGrouped - Cases grouped by status for Kanban
// =============================================================================

interface UseCasesGroupedResult {
  casesByStatus: CasesByStatus
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

const emptyCasesByStatus: CasesByStatus = {
  collection: [],
  analysis: [],
  review: [],
  reporting: [],
  closed: [],
  archived: [],
}

/**
 * Hook to fetch cases grouped by status for Kanban display
 */
export function useCasesGrouped(): UseCasesGroupedResult {
  const [casesByStatus, setCasesByStatus] = useState<CasesByStatus>(emptyCasesByStatus)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const data = await listCasesGrouped()
      setCasesByStatus(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch cases')
      setCasesByStatus(emptyCasesByStatus)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return { casesByStatus, loading, error, refetch: fetch }
}

// =============================================================================
// useCase - Single case
// =============================================================================

interface UseCaseResult {
  caseData: Case | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * Hook to fetch a single case by ID
 */
export function useCase(id: string | null): UseCaseResult {
  const [caseData, setCaseData] = useState<Case | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    if (!id) {
      setCaseData(null)
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const data = await getCase(id)
      setCaseData(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch case')
      setCaseData(null)
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return { caseData, loading, error, refetch: fetch }
}

// =============================================================================
// useCaseMutations - Create, update, delete cases
// =============================================================================

interface UseCaseMutationsResult {
  creating: boolean
  updating: boolean
  deleting: boolean
  error: string | null
  create: (input: CreateCaseInput) => Promise<Case | null>
  update: (id: string, input: UpdateCaseInput) => Promise<Case | null>
  updateStatus: (id: string, status: CaseStatus) => Promise<Case | null>
  remove: (id: string) => Promise<boolean>
  clearError: () => void
}

/**
 * Hook for case mutations (create, update, delete)
 */
export function useCaseMutations(): UseCaseMutationsResult {
  const [creating, setCreating] = useState(false)
  const [updating, setUpdating] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const create = useCallback(async (input: CreateCaseInput): Promise<Case | null> => {
    setCreating(true)
    setError(null)

    try {
      const result = await createCase(input)
      return result
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create case')
      return null
    } finally {
      setCreating(false)
    }
  }, [])

  const update = useCallback(async (id: string, input: UpdateCaseInput): Promise<Case | null> => {
    setUpdating(true)
    setError(null)

    try {
      const result = await updateCase(id, input)
      return result
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update case')
      return null
    } finally {
      setUpdating(false)
    }
  }, [])

  const updateStatusFn = useCallback(
    async (id: string, status: CaseStatus): Promise<Case | null> => {
      setUpdating(true)
      setError(null)

      try {
        const result = await updateCaseStatus(id, status)
        return result
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update case status')
        return null
      } finally {
        setUpdating(false)
      }
    },
    []
  )

  const remove = useCallback(async (id: string): Promise<boolean> => {
    setDeleting(true)
    setError(null)

    try {
      await deleteCase(id)
      return true
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete case')
      return false
    } finally {
      setDeleting(false)
    }
  }, [])

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  return {
    creating,
    updating,
    deleting,
    error,
    create,
    update,
    updateStatus: updateStatusFn,
    remove,
    clearError,
  }
}

// =============================================================================
// useCaseTeam - Team members for a case
// =============================================================================

interface UseCaseTeamResult {
  members: CaseTeamMember[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
  addMember: (userId: string, role: CaseTeamRole) => Promise<CaseTeamMember | null>
  removeMember: (userId: string) => Promise<boolean>
}

/**
 * Hook to manage case team members
 */
export function useCaseTeam(caseId: string | null): UseCaseTeamResult {
  const [members, setMembers] = useState<CaseTeamMember[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    if (!caseId) {
      setMembers([])
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const data = await listCaseTeam(caseId)
      setMembers(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch team members')
      setMembers([])
    } finally {
      setLoading(false)
    }
  }, [caseId])

  useEffect(() => {
    void fetch()
  }, [fetch])

  const addMemberFn = useCallback(
    async (userId: string, role: CaseTeamRole): Promise<CaseTeamMember | null> => {
      if (!caseId) return null

      setError(null)

      try {
        const member = await addTeamMember(caseId, userId, role)
        setMembers((prev) => [...prev, member])
        return member
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to add team member')
        return null
      }
    },
    [caseId]
  )

  const removeMemberFn = useCallback(
    async (userId: string): Promise<boolean> => {
      if (!caseId) return false

      setError(null)

      try {
        await removeTeamMember(caseId, userId)
        setMembers((prev) => prev.filter((member) => member.userId !== userId))
        return true
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to remove team member')
        return false
      }
    },
    [caseId]
  )

  return {
    members,
    loading,
    error,
    refetch: fetch,
    addMember: addMemberFn,
    removeMember: removeMemberFn,
  }
}
